import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const TeamTab = () => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteError, setInviteError] = useState('');

  const teamMembers = [
  {
    id: 1,
    name: 'Sarah Mitchell',
    email: 'sarah.mitchell@webforms.biz',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14da91c34-1763294780479.png",
    avatarAlt: 'Professional headshot of Sarah Mitchell with shoulder-length brown hair wearing navy blazer',
    role: 'owner',
    status: 'active',
    joinedDate: '2025-06-15',
    lastActive: '2026-01-30'
  },
  {
    id: 2,
    name: 'Michael Chen',
    email: 'michael.chen@webforms.biz',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd15b436-1763300581767.png",
    avatarAlt: 'Professional headshot of Asian man with short black hair wearing gray suit and glasses',
    role: 'admin',
    status: 'active',
    joinedDate: '2025-08-22',
    lastActive: '2026-01-29'
  },
  {
    id: 3,
    name: 'Emily Rodriguez',
    email: 'emily.rodriguez@webforms.biz',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_162a57531-1763296100992.png",
    avatarAlt: 'Professional headshot of Hispanic woman with long dark hair wearing white blouse',
    role: 'member',
    status: 'active',
    joinedDate: '2025-10-10',
    lastActive: '2026-01-28'
  },
  {
    id: 4,
    name: 'David Thompson',
    email: 'david.thompson@webforms.biz',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12152daa9-1763295597331.png",
    avatarAlt: 'Professional headshot of Caucasian man with short blonde hair wearing blue shirt',
    role: 'member',
    status: 'pending',
    joinedDate: '2026-01-25',
    lastActive: null
  }];


  const roles = [
  {
    id: 'owner',
    name: 'Owner',
    description: 'Full access to all features including billing and team management',
    permissions: ['All permissions', 'Manage billing', 'Delete workspace']
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Manage forms, team members, and integrations',
    permissions: ['Create/edit forms', 'Manage team', 'View analytics', 'Manage integrations']
  },
  {
    id: 'member',
    name: 'Member',
    description: 'Create and edit forms with limited administrative access',
    permissions: ['Create/edit forms', 'View analytics', 'Export data']
  },
  {
    id: 'viewer',
    name: 'Viewer',
    description: 'View-only access to forms and analytics',
    permissions: ['View forms', 'View analytics']
  }];


  const handleInvite = () => {
    if (!inviteEmail?.trim()) {
      setInviteError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(inviteEmail)) {
      setInviteError('Invalid email format');
      return;
    }
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('member');
    setInviteError('');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-primary/10 text-primary';
      case 'admin':
        return 'bg-accent/10 text-accent';
      case 'member':
        return 'bg-secondary/10 text-secondary';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-success/10 text-success text-xs font-medium rounded-md">
          <Icon name="CheckCircle2" size={14} />
          Active
        </span>);

    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-warning/10 text-warning text-xs font-medium rounded-md">
        <Icon name="Clock" size={14} />
        Pending
      </span>);

  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
              Team Members
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Manage your team and their permissions
            </p>
          </div>
          <Button
            variant="default"
            size="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowInviteModal(true)}>

            Invite Member
          </Button>
        </div>

        <div className="space-y-4">
          {teamMembers?.map((member) =>
          <div
            key={member?.id}
            className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-smooth">

              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-border flex-shrink-0">
                  <Image
                  src={member?.avatar}
                  alt={member?.avatarAlt}
                  className="w-full h-full object-cover" />

                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm md:text-base font-medium text-foreground truncate">
                    {member?.name}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground truncate">
                    {member?.email}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 lg:gap-4">
                <span className={`px-3 py-1.5 text-xs font-medium rounded-md ${getRoleBadgeColor(member?.role)}`}>
                  {member?.role?.charAt(0)?.toUpperCase() + member?.role?.slice(1)}
                </span>
                {getStatusBadge(member?.status)}
                <div className="flex items-center gap-2">
                  {member?.role !== 'owner' &&
                <>
                      <Button variant="ghost" size="sm" iconName="Edit2" />
                      <Button variant="ghost" size="sm" iconName="Trash2" />
                    </>
                }
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-6">
          Role Permissions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {roles?.map((role) =>
          <div key={role?.id} className="bg-muted/50 rounded-lg p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                    {role?.name}
                  </h3>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {role?.description}
              </p>
              <ul className="space-y-2">
                {role?.permissions?.map((permission, index) =>
              <li key={index} className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{permission}</span>
                  </li>
              )}
              </ul>
            </div>
          )}
        </div>
      </div>
      {showInviteModal &&
      <div className="fixed inset-0 bg-background/80 z-[1040] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-elevation-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Invite Team Member
              </h3>
              <button
              onClick={() => {
                setShowInviteModal(false);
                setInviteEmail('');
                setInviteError('');
              }}
              className="w-8 h-8 rounded-md hover:bg-muted transition-smooth flex items-center justify-center">

                <Icon name="X" size={20} color="var(--color-muted-foreground)" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <Input
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => {
                setInviteEmail(e?.target?.value);
                setInviteError('');
              }}
              error={inviteError}
              placeholder="colleague@company.com"
              required />


              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Role
                </label>
                <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth">

                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <Button
              variant="outline"
              size="default"
              onClick={() => {
                setShowInviteModal(false);
                setInviteEmail('');
                setInviteError('');
              }}>

                Cancel
              </Button>
              <Button
              variant="default"
              size="default"
              iconName="Send"
              iconPosition="left"
              onClick={handleInvite}>

                Send Invitation
              </Button>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default TeamTab;